// Shared TrackedItemRow primitive.
//
// Captures the common UX scaffolding across row-based deal-flow workflows:
// a status chip, a detailed-context popup modal, a notes textarea, and a
// universal escalation path. An optional judgment plugin layers on the
// severity-assessment + report-attestation + Certify/Decline/Escalate trio
// for certification-style rows (no consumer enables it yet).
//
// The current consumers (Intake Checklist, Document Requests) use the
// primitive with the judgment plugin DISABLED — they are process-tracking workflows,
// not certification workflows.

import type { ReactNode } from "react";

/** A workflow state value. Narrowed per consumer via a StateSet. */
export type TrackedItemState = string;

/**
 * Chip palette tokens. These are the primitive's stable vocabulary; the
 * chip component maps each to the closest design-system Badge tone so the
 * chrome stays visually identical to the analyst-certification rows.
 */
export type ChipVariant = "neutral" | "amber" | "blue" | "green" | "red" | "muted";

export interface TrackedItemStateDef<S extends TrackedItemState = TrackedItemState> {
  value: S;
  /** Display label, e.g. "Pending", "Approved". */
  label: string;
  chipVariant: ChipVariant;
  /** Terminal states disable further state changes (unless reopened via admin undo). */
  isTerminal: boolean;
}

export interface StateSet<S extends TrackedItemState = TrackedItemState> {
  states: Array<TrackedItemStateDef<S>>;
  /** Which states each state may transition to. */
  transitions: Record<S, S[]>;
  /**
   * TrackedItemRow post-escalation — the working state an Escalated item
   * returns to when its escalation is Denied (rework). It is also one of the
   * item's exits once the escalation is Approved (`escalationExits`). Omit for
   * consumers with no escalation path.
   */
  reworkState?: S;
}

/**
 * TrackedItemRow post-escalation — the states an Escalated item may move to
 * once an executive approves its escalation: the rework state and every state
 * the normal transitions reach, in the state set's order. Escalated itself is
 * never an exit, and a consumer with no rework state has none.
 *
 * The servers behind both consumers (IntakeChecklistService.UpsertItemStatusAsync,
 * PreApprovalDocumentRequestService.AttestAsync) read no directive: once the
 * escalation is resolved they accept any status. So the row offers every exit
 * whatever the directive says (owner ruling, 2026-10-26: the screen offers what
 * the server allows). Pinned by tests/fixtures/escalation-directive-cases.json.
 */
export function escalationExits(stateSet: StateSet): TrackedItemState[] {
  if (!stateSet.reworkState) return [];
  const reachable = new Set<TrackedItemState>([
    stateSet.reworkState,
    ...Object.values(stateSet.transitions).flat(),
  ]);
  return stateSet.states
    .map((s) => s.value)
    .filter((value) => value !== "Escalated" && reachable.has(value));
}

/** Severity assessment vocabulary, mirrors the criteria-checklist CriteriaItemAssessment. */
export type JudgmentAssessment = "Clean" | "Minor" | "Material" | "Critical";

/** Escalation lifecycle as surfaced from an EscalationRecord. */
export type TrackedItemEscalationStatus = "None" | "Pending" | "Approved" | "Denied";

/**
 * Opt-in plugin that turns a TrackedItemRow into a certification row:
 * a severity-assessment dropdown, a report-uploaded attestation checkbox,
 * and the Certify / Decline / Escalate three-button trio. Both current
 * consumers leave this undefined.
 */
export interface JudgmentPluginConfig {
  assessment: {
    value: JudgmentAssessment | null;
    onChange: (v: JudgmentAssessment | null) => void;
    required: boolean;
  };
  reportUploaded: {
    value: boolean;
    attestedAt: string | null;
    attestedByDisplayName: string | null;
    onChange: (v: boolean) => void;
    required: boolean;
  };
  justification: {
    value: string;
    onChange: (v: string) => void;
    required: boolean;
  };
  onCertify: () => void;
  onDecline: () => void;
}

export interface TrackedItemMetadataEntry {
  label: string;
  value: string;
}

export interface TrackedItemRowProps {
  // ---- Content -------------------------------------------------------
  title: string;
  /** Optional short code chip, e.g. "F-DK1" for analyst items. */
  code?: string;
  description?: string;
  /** Required-evidence prose surfaced in the popup. */
  requiredEvidence?: string;
  /** Free-text escalation-path description, e.g. "Escalates to LA, then Admin." */
  escalationPath?: string;
  /** Per-context metadata rendered in the popup (party + due date, etc.). */
  metadata?: TrackedItemMetadataEntry[];
  /** Optional extra content rendered inside the popup body. */
  detailExtra?: ReactNode;

  // ---- State ---------------------------------------------------------
  state: TrackedItemState;
  stateSet: StateSet;
  onStateChange: (newState: TrackedItemState) => void;
  /** Disables the state-change control while a mutation is in flight. */
  stateChangePending?: boolean;

  // ---- Notes ---------------------------------------------------------
  notes: string;
  onNotesChange: (notes: string) => void;
  notesSavePending?: boolean;

  // ---- Escalation ----------------------------------------------------
  /** Gated to the right permission by the consumer. */
  canEscalate: boolean;
  escalationStatus: TrackedItemEscalationStatus;
  /**
   * Free-text resolution directive from the resolved EscalationRecord. Shown in
   * the tooltip of the row's escalation action; it does not narrow the exits,
   * as the server does not read it.
   */
  escalationDirective?: string | null;
  /** ISO timestamp the escalation was resolved; surfaced in the action tooltip. */
  escalationResolvedAt?: string | null;
  onEscalate: (reason: string) => void;
  escalatePending?: boolean;

  // ---- Optional judgment plugin --------------------------------------
  judgmentPlugin?: JudgmentPluginConfig;

  /** Muted, non-interactive rendering (e.g. deal past the working stage). */
  isReadOnly?: boolean;
}

// ----------------------------------------------------------------------
// Consumer-specific state-set definitions.
// ----------------------------------------------------------------------

/**
 * Intake Checklist (C1 / Broker Liaison). The frontend state set maps the
 * persisted ChecklistItemStatus enum (NotStarted / InProgress / Complete /
 * NotApplicable) plus the new Escalated state. NotStarted/InProgress both
 * surface as process-tracking chrome; Complete / NotApplicable / Escalated
 * are terminal for the purposes of the primitive's state-change control.
 */
export const INTAKE_STATE_SET: StateSet = {
  states: [
    { value: "NotStarted", label: "Pending", chipVariant: "neutral", isTerminal: false },
    { value: "InProgress", label: "In Progress", chipVariant: "blue", isTerminal: false },
    { value: "Complete", label: "Complete", chipVariant: "green", isTerminal: true },
    { value: "Escalated", label: "Escalated", chipVariant: "amber", isTerminal: true },
    { value: "NotApplicable", label: "N/A", chipVariant: "muted", isTerminal: true },
  ],
  // Escalated is reached via the dedicated Escalate action, never the
  // state-change control — so it is absent from every transition list.
  transitions: {
    NotStarted: ["InProgress", "Complete", "NotApplicable"],
    InProgress: ["Complete", "NotApplicable"],
    Complete: [],
    Escalated: [],
    NotApplicable: [],
  },
  // Post-escalation — an Escalated item leaves Escalated only through the
  // escalation action: any exit once Approved, the rework state once Denied.
  reworkState: "NotStarted",
};

/**
 * Document Requests (B1 / Loan Administrator). Mirrors the persisted
 * PreApprovalRequestStatus progression and adds the universal Escalated
 * terminal. Draft surfaces as the "Pending" chip.
 */
export const DOC_REQUEST_STATE_SET: StateSet = {
  states: [
    { value: "Draft", label: "Pending", chipVariant: "neutral", isTerminal: false },
    { value: "Requested", label: "Requested", chipVariant: "blue", isTerminal: false },
    { value: "Reminded", label: "Reminded", chipVariant: "blue", isTerminal: false },
    { value: "Received", label: "Received", chipVariant: "amber", isTerminal: false },
    { value: "UnderReview", label: "Under Review", chipVariant: "amber", isTerminal: false },
    { value: "Approved", label: "Approved", chipVariant: "green", isTerminal: true },
    { value: "Rejected", label: "Rejected", chipVariant: "red", isTerminal: false },
    { value: "Waived", label: "Waived", chipVariant: "muted", isTerminal: true },
    { value: "Escalated", label: "Escalated", chipVariant: "amber", isTerminal: true },
  ],
  // Escalated is reached via the dedicated Escalate action, never the
  // state-change control — so it is absent from every transition list.
  transitions: {
    Draft: ["Requested", "Waived"],
    Requested: ["Received", "Waived"],
    Reminded: ["Received", "Waived"],
    Received: ["Approved", "Rejected"],
    UnderReview: ["Approved", "Rejected"],
    Approved: [],
    Rejected: ["Received", "Waived"],
    Waived: [],
    Escalated: [],
  },
  // Post-escalation — an Escalated request leaves Escalated only through the
  // escalation action: any exit once Approved, the rework state once Denied.
  reworkState: "Requested",
};

/** Resolve a state definition; falls back to a neutral chip for unknown values. */
export function resolveStateDef(
  stateSet: StateSet,
  value: TrackedItemState,
): TrackedItemStateDef {
  return (
    stateSet.states.find((s) => s.value === value) ?? {
      value,
      label: value,
      chipVariant: "neutral",
      isTerminal: true,
    }
  );
}
